/* ================================================================
   Katalogsicht („Alle Weine“).

   Ursprünglich nur für Kuratorenkonten gebaut, inzwischen für jeden
   Nutzer über den Umschalter in der Werkzeugleiste erreichbar.

   Ein Kuratorenkonto (GLOBAL_NUTZER) soll die Stammdaten pflegen
   können, ohne selbst Flaschen zu besitzen. Deshalb wird für die
   Anzeige das eigene Kellerbuch mit dem gesamten Katalog gemischt:
   Was im Katalog steht und lokal fehlt, erscheint als Eintrag mit
   Bestand null.

   Zwei Dinge sind dabei entscheidend:

   1. Das Ergebnis ist eine reine Anzeigesicht. Es wird NICHT
      gespeichert — sonst wüchse das Kellerbuch um den ganzen Katalog
      und die Synchronisation würde ihn bei jedem Speichern hochladen.
      (Vorschläge entstünden dabei nicht, weil die gemischten Objekte
      inhaltlich dem Katalog entsprechen — aber das Kellerbuch wäre
      dennoch mit fremden Stammdaten aufgeblasen.)
   2. Die Objekte sind als `nurKatalog` gekennzeichnet. Bearbeitet der
      Kurator eines davon, übernimmt der Speicherweg es in diesem
      Moment in das eigene Kellerbuch.
   ================================================================ */

/* Kennzeichnet Objekte, die nur aus dem Katalog stammen. */
const KATALOG_MARKE = { herkunft: "katalog", nurKatalog: true };

/**
 * Kellerbuch und Katalog zu einer Anzeigesicht mischen.
 *
 * Fremde Kritiken werden hier bewusst NICHT eingemischt. Sie gehen
 * getrennt an die Weinansicht, die sie hinter „Alle Kritiken
 * einblenden“ anbietet — sonst stünden sie ununterscheidbar zwischen
 * den eigenen, und im eigenen Keller (ohne Katalogsicht) gäbe es sie
 * überhaupt nicht.
 */
function mischeKatalog(db, katalog) {
  if (!db || !katalog) return db;

  const eigeneRegionen = new Set((db.regions || []).map((r) => r.id));
  const eigeneWinzer = new Set((db.winzer || []).map((w) => w.id));
  const eigeneWeine = new Set((db.weine || []).map((w) => w.id));

  const regionen = [...(db.regions || [])];
  for (const r of katalog.regionen || []) {
    if (eigeneRegionen.has(r.id)) continue;
    regionen.push({ ...r, ...KATALOG_MARKE });
  }

  const winzer = [...(db.winzer || [])];
  for (const w of katalog.winzer || []) {
    if (eigeneWinzer.has(w.id)) continue;
    winzer.push({ ...w, ...KATALOG_MARKE });
  }

  const weine = [...(db.weine || [])];
  for (const k of katalog.weine || []) {
    if (eigeneWeine.has(k.id)) continue;
    const rl = Array.isArray(k.rebsortenListe) ? k.rebsortenListe : [];
    const anzeige = rl.length
      ? (rl.every((r) => r.anteil != null)
        ? rl.map((r) => `${r.name} ${Math.round(r.anteil)} %`)
        : rl.map((r) => r.name)).join(" · ")
      : "";
    weine.push({
      ...k, rebsorten: anzeige, rebsortenListe: rl, ...KATALOG_MARKE,
      /* Bestände sind leer — die Flaschen gehören ja niemandem hier. */
      jahrgaenge: (k.jahrgaenge || []).map((j) => ({
        id: j.id, jahr: j.jahr,
        trinkVon: j.trinkVon != null ? j.trinkVon : null,
        trinkBis: j.trinkBis != null ? j.trinkBis : null,
        katalogFoto: j.foto || null,
        bestaende: {}, lagerort: "", preis: null,
      })),
    });
  }

  return { ...db, regions: regionen, winzer, weine };
}

/* Ersetzt einen Eintrag oder legt ihn an, falls er nur im Katalog
   stand. Damit greift das Bearbeiten auch bei Objekten, die noch
   nicht im eigenen Kellerbuch stehen. */
function ersetzeOderErgaenze(liste, id, werte, vorlage = null) {
  const vorhanden = (liste || []).some((o) => o.id === id);
  if (vorhanden) {
    return (liste || []).map((o) => o.id === id ? { ...o, ...werte, nurKatalog: undefined } : o);
  }
  const grundlage = vorlage ? { ...vorlage, nurKatalog: undefined } : { id };
  return [...(liste || []), { ...grundlage, id, ...werte }];
}

export { ersetzeOderErgaenze, mischeKatalog };
